import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { SelectedItem, RivhitItem, SelectedCustomer } from './types';
import { getRivhitItemPricing, getCustomerRecentItems } from '../../api/client';
import { pickBestCustomerPrice } from '@/lib/pricing';

interface ItemsStepProps {
  items: SelectedItem[];
  onChange: (items: SelectedItem[]) => void;
  rivhitItems: RivhitItem[];
  rivhitItemsLoading: boolean;
  customer?: SelectedCustomer | null;
}

function fuzzyMatchItem(item: RivhitItem, query: string): boolean {
  const q = query.toLowerCase();
  return [
    item.item_name,
    item.catalog_number,
    item.item_group_name,
    item.barcode,
  ].some((field) => field?.toLowerCase().includes(q));
}

export default function ItemsStep({ items, onChange, rivhitItems, rivhitItemsLoading, customer }: ItemsStepProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customDesc, setCustomDesc] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [customUnit, setCustomUnit] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const previousCustomerIdRef = useRef<number | null | undefined>(undefined);
  const [recentItemIds, setRecentItemIds] = useState<Set<number>>(new Set());
  const [recentItemsLoading, setRecentItemsLoading] = useState(false);

  // Fetch recent item IDs when customer changes
  useEffect(() => {
    const customerId = customer?.customer_id;
    if (!customerId) {
      setRecentItemIds(new Set());
      return;
    }

    let cancelled = false;
    setRecentItemsLoading(true);

    getCustomerRecentItems(customerId)
      .then((res) => {
        if (!cancelled) {
          setRecentItemIds(new Set(res.data?.item_ids ?? []));
        }
      })
      .catch(() => {
        if (!cancelled) setRecentItemIds(new Set());
      })
      .finally(() => {
        if (!cancelled) setRecentItemsLoading(false);
      });

    return () => { cancelled = true; };
  }, [customer?.customer_id]);

  // Debounce query
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(timer);
  }, [query]);

  // Client-side fuzzy filtering — show all when no query, sort recent items first
  const filtered = useMemo(() => {
    let result = rivhitItems;
    if (debouncedQuery.trim()) {
      result = rivhitItems.filter((item) => fuzzyMatchItem(item, debouncedQuery));
    }
    if (recentItemIds.size === 0) return result;
    // Sort recent items to the top
    return [...result].sort((a, b) => {
      const aRecent = a.item_id != null && recentItemIds.has(a.item_id) ? 0 : 1;
      const bRecent = b.item_id != null && recentItemIds.has(b.item_id) ? 0 : 1;
      return aRecent - bRecent;
    });
  }, [rivhitItems, debouncedQuery, recentItemIds]);

  const { visible: visibleItems, hasMore, sentinelRef } = useInfiniteScroll(filtered, 50);

  const resolveItemPrice = useCallback(
    async (itemId: number | undefined, quantity: number, catalogPrice: number | undefined): Promise<number> => {
      const fallbackPrice = catalogPrice ?? 0;
      if (!itemId || !customer?.customer_id) return fallbackPrice;

      try {
        const response = await getRivhitItemPricing(itemId, customer.customer_id);
        return pickBestCustomerPrice(response.data ?? [], quantity, fallbackPrice);
      } catch {
        return fallbackPrice;
      }
    },
    [customer, pickBestCustomerPrice],
  );

  const addRivhitItem = async (rivhitItem: RivhitItem) => {
    const catalogNum = rivhitItem.catalog_number || String(rivhitItem.item_id);
    const exists = items.find((i) => i.catalog_number === catalogNum);
    if (exists) return;

    const price = await resolveItemPrice(
      rivhitItem.item_id,
      1,
      rivhitItem.price ?? undefined,
    );

    const item: SelectedItem = {
      id: crypto.randomUUID(),
      item_id: rivhitItem.item_id,
      catalog_number: catalogNum,
      description: rivhitItem.item_name || '',
      quantity: 1,
      price,
      unit: rivhitItem.quantity_type || '',
      is_custom: false,
    };
    onChange([...items, item]);
  };

  useEffect(() => {
    const customerId = customer?.customer_id ?? null;

    if (previousCustomerIdRef.current === undefined) {
      previousCustomerIdRef.current = customerId;
      return;
    }

    if (previousCustomerIdRef.current === customerId) {
      return;
    }

    previousCustomerIdRef.current = customerId;

    const repricableItems = items.filter((item) => !item.is_custom);
    if (repricableItems.length === 0) {
      return;
    }

    let cancelled = false;

    const repriceItems = async () => {
      const repriced = await Promise.all(
        items.map(async (item) => {
          if (item.is_custom) return item;

          const rivhitItem = item.item_id != null
            ? rivhitItems.find((candidate) => candidate.item_id === item.item_id)
            : rivhitItems.find((candidate) => {
                const candidateCatalog = candidate.catalog_number || String(candidate.item_id);
                return candidateCatalog === item.catalog_number;
              });

          const price = await resolveItemPrice(
            item.item_id ?? rivhitItem?.item_id,
            item.quantity,
            rivhitItem?.price ?? item.price,
          );

          return { ...item, item_id: item.item_id ?? rivhitItem?.item_id, price };
        }),
      );

      if (!cancelled) {
        onChange(repriced);
      }
    };

    void repriceItems();

    return () => {
      cancelled = true;
    };
  }, [customer, items, onChange, resolveItemPrice, rivhitItems]);

  const addCustomItem = () => {
    if (!customDesc.trim()) return;
    const item: SelectedItem = {
      id: crypto.randomUUID(),
      description: customDesc.trim(),
      quantity: 1,
      price: parseFloat(customPrice) || 0,
      unit: customUnit.trim(),
      is_custom: true,
    };
    onChange([...items, item]);
    setCustomDesc('');
    setCustomPrice('');
    setCustomUnit('');
    setShowCustomForm(false);
  };

  const updateItem = async (id: string, field: keyof SelectedItem, value: string | number) => {
    if (field !== 'quantity') {
      onChange(items.map((i) => (i.id === id ? { ...i, [field]: value } : i)));
      return;
    }

    const nextQuantity = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
    const updatedItems = await Promise.all(
      items.map(async (item) => {
        if (item.id !== id) return item;

        const nextItem = { ...item, quantity: nextQuantity };
        if (item.is_custom) return nextItem;

        const rivhitItem = item.item_id != null
          ? rivhitItems.find((candidate) => candidate.item_id === item.item_id)
          : rivhitItems.find((candidate) => {
              const candidateCatalog = candidate.catalog_number || String(candidate.item_id);
              return candidateCatalog === item.catalog_number;
            });

        const price = await resolveItemPrice(
          item.item_id ?? rivhitItem?.item_id,
          nextQuantity,
          rivhitItem?.price ?? item.price,
        );

        return {
          ...nextItem,
          item_id: item.item_id ?? rivhitItem?.item_id,
          price,
        };
      }),
    );

    onChange(updatedItems);
  };

  const removeItem = (id: string) => {
    onChange(items.filter((i) => i.id !== id));
  };

  const total = items.reduce((sum, i) => sum + i.quantity * i.price, 0);

  // Check if a Rivhit item is already selected
  const isItemSelected = (rivhitItem: RivhitItem) => {
    const catalogNum = rivhitItem.catalog_number || String(rivhitItem.item_id);
    return items.some((i) => i.catalog_number === catalogNum);
  };

  return (
    <div className="space-y-6">
      {/* Search input */}
      <div className="relative">
        <span className="absolute inset-y-0 ltr:left-4 rtl:right-4 flex items-center text-gray-400 pointer-events-none">
          🔍
        </span>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('createDocument.items.searchPlaceholder', 'Search products...')}
          className="w-full rounded-xl border border-gray-300 p-3 ltr:pl-11 rtl:pr-11 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition"
        />
      </div>

      <div className="flex items-center gap-2 text-sm text-gray-400">
        {rivhitItemsLoading || recentItemsLoading ? (
          <span className="inline-flex items-center gap-1 text-blue-400">
            <span className="animate-spin inline-block h-3 w-3 border-2 border-blue-300 border-t-blue-500 rounded-full" />
            {recentItemsLoading && !rivhitItemsLoading
              ? t('createDocument.items.loadingRecentItems', 'Loading recent items...')
              : t('createDocument.items.loadingItems', 'Loading items...')}
          </span>
        ) : (
          <span>
            {filtered.length} {t('customers.resultsFound', 'results found')}
            {recentItemIds.size > 0 && (
              <span className="text-amber-600 ltr:ml-2 rtl:mr-2">
                ({recentItemIds.size} {t('createDocument.items.recentCount', 'recent')})
              </span>
            )}
          </span>
        )}
      </div>

      {/* Scrollable Rivhit items list */}
      <div
        ref={listRef}
        className="max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white divide-y divide-gray-100"
      >
        {filtered.length === 0 && !rivhitItemsLoading && (
          <div className="py-8 text-center text-gray-400 text-sm">
            {t('common.noResults', 'No results found')}
          </div>
        )}
        {visibleItems.map((rivhitItem, index) => {
          const selected = isItemSelected(rivhitItem);
          const isRecent = rivhitItem.item_id != null && recentItemIds.has(rivhitItem.item_id);
          const prevItem = index > 0 ? visibleItems[index - 1] : null;
          const prevIsRecent = prevItem?.item_id != null && recentItemIds.has(prevItem.item_id);
          const showSeparator = recentItemIds.size > 0 && !isRecent && (index === 0 || prevIsRecent);
          return (
            <div key={rivhitItem.item_id ?? rivhitItem.catalog_number}>
              {/* Section header for recent items */}
              {index === 0 && isRecent && (
                <div className="px-4 py-1.5 bg-amber-50 text-amber-700 text-xs font-semibold border-b border-amber-200">
                  ⏱ {t('createDocument.items.recentItems', 'Recent Items')}
                </div>
              )}
              {/* Separator between recent and other items */}
              {showSeparator && (
                <div className="px-4 py-1.5 bg-gray-50 text-gray-500 text-xs font-semibold border-b border-gray-200">
                  {t('createDocument.items.allItems', 'All Items')}
                </div>
              )}
              <div
                onClick={() => !selected && addRivhitItem(rivhitItem)}
                className={[
                  'py-3 px-4 transition-colors flex items-center justify-between gap-3',
                  selected
                    ? 'bg-green-50 cursor-default'
                    : isRecent
                      ? 'cursor-pointer hover:bg-amber-50 bg-amber-50/30'
                      : 'cursor-pointer hover:bg-blue-50',
                ].join(' ')}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">
                    {rivhitItem.item_name || t('createDocument.items.noName', 'Unnamed item')}
                  </p>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-sm text-gray-500">
                    {rivhitItem.catalog_number && (
                      <span>#{rivhitItem.catalog_number}</span>
                    )}
                    {rivhitItem.item_group_name && (
                      <span className="bg-gray-100 px-2 py-0.5 rounded-full text-xs">
                        {rivhitItem.item_group_name}
                      </span>
                    )}
                    {rivhitItem.quantity_type && (
                      <span>{rivhitItem.quantity_type}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {isRecent && !selected && (
                    <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-medium">
                      {t('createDocument.items.recent', 'Recent')}
                    </span>
                  )}
                  {rivhitItem.price != null && rivhitItem.price > 0 && (
                    <span className="text-blue-600 font-bold text-sm">
                      ₪{Number(rivhitItem.price).toFixed(2)}
                    </span>
                  )}
                  {selected && (
                    <span className="text-green-600 text-sm font-medium">✓</span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {hasMore && (
          <div ref={sentinelRef} className="py-2 text-center text-xs text-gray-400">
            {visibleItems.length} / {filtered.length}
          </div>
        )}
      </div>

      {/* Selected items */}
      <div className="space-y-3">
        <h3 className="font-semibold text-gray-700">
          {t('createDocument.items.selectedItems', 'Selected Items')}
          {items.length > 0 && (
            <span className="text-sm font-normal text-gray-400 ltr:ml-2 rtl:mr-2">({items.length})</span>
          )}
        </h3>

        {items.length === 0 && !showCustomForm && (
          <div className="text-center py-10 text-gray-400 text-sm">
            {t(
              'createDocument.items.empty',
              'No items added yet. Search products above or add a custom item.',
            )}
          </div>
        )}

        {items.map((item) => (
          <div
            key={item.id}
            className="flex flex-wrap items-center gap-3 p-3 rounded-xl border border-gray-200 bg-white"
          >
            {/* Description */}
            <div className="flex-1 min-w-[140px]">
              <label htmlFor={`item-description-${item.id}`} className="text-sm text-gray-500 block mb-1">
                {t('createDocument.items.description', 'Description')}
              </label>
              <input
                id={`item-description-${item.id}`}
                type="text"
                value={item.description}
                onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                placeholder={t('createDocument.items.customDescPlaceholder', 'Item description')}
                className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-800 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>

            {/* Quantity */}
            <div className="w-24">
              <label className="text-xs text-gray-500 block">
                {t('createDocument.items.quantity', 'Qty')}
              </label>
              <input
                type="number"
                min={0.01}
                step={0.01}
                value={item.quantity}
                onChange={(e) => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>

            {/* Price */}
            <div className="w-28">
              <label className="text-xs text-gray-500 block">
                {t('createDocument.items.price', 'Price')}
              </label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={item.price}
                onChange={(e) => updateItem(item.id, 'price', parseFloat(e.target.value) || 0)}
                className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>

            {/* Unit */}
            {item.unit && (
              <div className="w-16 text-center">
                <span className="text-xs text-gray-500 block">
                  {t('createDocument.items.unit', 'Unit')}
                </span>
                <span className="text-sm text-gray-600">{item.unit}</span>
              </div>
            )}

            {/* Line total */}
            <div className="w-28 text-end">
              <span className="text-xs text-gray-500 block">
                {t('createDocument.items.lineTotal', 'Total')}
              </span>
              <span className="font-bold text-gray-800">
                ₪{Number(item.quantity * item.price).toFixed(2)}
              </span>
            </div>

            {/* Remove */}
            <button
              type="button"
              onClick={() => removeItem(item.id)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition"
              aria-label={t('createDocument.items.remove', 'Remove')}
            >
              ✕
            </button>
          </div>
        ))}

        {/* Custom item form */}
        {showCustomForm ? (
          <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl border-2 border-dashed border-blue-300 bg-blue-50/40">
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-gray-600 block mb-1">
                {t('createDocument.items.description', 'Description')}
              </label>
              <input
                type="text"
                value={customDesc}
                onChange={(e) => setCustomDesc(e.target.value)}
                placeholder={t('createDocument.items.customDescPlaceholder', 'Item description')}
                className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                autoFocus
              />
            </div>
            <div className="w-28">
              <label className="text-xs text-gray-600 block mb-1">
                {t('createDocument.items.price', 'Price')}
              </label>
              <input
                type="number"
                min={0}
                step={0.01}
                value={customPrice}
                onChange={(e) => setCustomPrice(e.target.value)}
                placeholder="0.00"
                className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <div className="w-24">
              <label className="text-xs text-gray-600 block mb-1">
                {t('createDocument.items.unit', 'Unit')}
              </label>
              <input
                type="text"
                value={customUnit}
                onChange={(e) => setCustomUnit(e.target.value)}
                placeholder={t('createDocument.items.unitPlaceholder', 'kg, pcs...')}
                className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={addCustomItem}
                disabled={!customDesc.trim()}
                className="px-4 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {t('createDocument.items.add', 'Add')}
              </button>
              <button
                type="button"
                onClick={() => setShowCustomForm(false)}
                className="px-3 py-1.5 rounded-lg text-sm text-gray-500 hover:bg-gray-100 transition"
              >
                {t('createDocument.items.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowCustomForm(true)}
            className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-gray-300 text-gray-400 hover:border-blue-400 hover:text-blue-500 transition cursor-pointer"
          >
            <span className="text-xl leading-none">+</span>
            <span className="text-sm font-medium">
              {t('createDocument.items.addCustom', 'Add Custom Item')}
            </span>
          </button>
        )}
      </div>

      {/* Running total */}
      {items.length > 0 && (
        <div className="sticky bottom-0 flex items-center justify-between p-4 rounded-xl bg-gray-50 border border-gray-200 shadow-sm">
          <span className="text-gray-600 font-medium">
            {t('createDocument.items.total', 'Total')}
          </span>
          <span className="text-lg font-bold text-gray-900">₪{Number(total).toFixed(2)}</span>
        </div>
      )}
    </div>
  );
}
